import React from 'react';
import Modal from '../shared/Modal';
import StatusPill from '../shared/StatusPill';
import WorkflowActions from '../../shared/WorkflowActions';
import { useAuth } from '../../../context/AuthContext';
import { formatDateTime, formatNumber, humanize, orDash } from '../shared/format';

function DetailRow({ label, value, mono = false, wide = false }) {
  return (
    <div className={wide ? 'grid gap-2 sm:col-span-2 lg:col-span-3' : 'grid gap-2'}>
      <span className="text-[13px] font-semibold text-gray-900">{label}</span>
      <div
        className={`rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-700 ${
          mono ? 'font-mono text-[12px]' : ''
        }`}
      >
        {value === null || value === undefined || value === '' ? '—' : value}
      </div>
    </div>
  );
}

function formatActor(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    return value.name ?? value.label ?? value.uuid ?? '—';
  }
  return String(value);
}

function formatLocation(row) {
  if (!row) return '—';
  if (String(row.geographic_scope ?? '').toLowerCase() === 'national') return 'National';
  const parts = [row.state, row.lga].map(formatActor).filter((part) => part !== '—');
  return parts.length > 0 ? parts.join(' / ') : '—';
}

function formatDate(value) {
  return value ? formatDateTime(value) : '—';
}

export default function SupplementaryDatasetDetailsModal({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onWorkflowAction,
  row,
}) {
  const { permissions = [] } = useAuth();

  if (!isOpen) return null;

  const handleEdit = () => {
    if (onEdit) {
      onEdit(row);
      return;
    }
    onClose();
  };

  const handleDelete = () => {
    if (onDelete) onDelete(row);
  };

  return (
    <Modal
      title="Supplementary Dataset Details"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleEdit}
      submitLabel="Edit Record"
      successTitle="Dataset Details"
      successMessage=" "
      maxWidthClassName="max-w-[920px]"
    >
      <div className="flex items-start justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">
            {row?.dataset_name || 'Untitled dataset'}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            UUID: {row?.uuid || '—'}
          </p>
        </div>
        <StatusPill value={row?.data_status || 'draft'} dot />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Source Organisation" value={orDash(row?.source_organisation)} />
        <DetailRow label="Indicator Reference" value={orDash(row?.indicator_reference)} />
        <DetailRow label="Reference Period" value={orDash(row?.reference_period)} />
        <DetailRow label="Geographic Scope" value={humanize(row?.geographic_scope)} />
        <DetailRow label="Location" value={formatLocation(row)} />
        <DetailRow label="Value Numeric" value={formatNumber(row?.value_numeric)} />
        <DetailRow label="Submitted By" value={formatActor(row?.submitted_by)} />
        <DetailRow label="Submitted At" value={formatDate(row?.submitted_at)} />
        <DetailRow label="Validated By" value={formatActor(row?.validated_by)} />
        <DetailRow label="Validated At" value={formatDate(row?.validated_at)} />
        <DetailRow label="Approved By" value={formatActor(row?.approved_by)} />
        <DetailRow label="Approved At" value={formatDate(row?.approved_at)} />
        <DetailRow label="Rejected At" value={formatDate(row?.rejected_at)} />
        <DetailRow label="Created At" value={formatDate(row?.created_at)} />
        <DetailRow label="Updated At" value={formatDate(row?.updated_at)} />
        <DetailRow label="Rejection Notes" value={orDash(row?.rejection_notes)} wide />
        <DetailRow label="Notes" value={orDash(row?.notes)} wide />
      </div>

      {onDelete && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleDelete}
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100"
          >
            Delete Record
          </button>
        </div>
      )}

      <div className="grid gap-2">
        <span className="text-[13px] font-semibold text-gray-900">Workflow</span>
        <WorkflowActions
          status={row?.data_status || 'draft'}
          permissions={permissions}
          onAction={onWorkflowAction}
        />
        <p className="text-xs text-gray-500">
          Use the workflow buttons to submit, validate, approve, or reject this dataset.
        </p>
      </div>
    </Modal>
  );
}
